/**
 * Agent A6a — Gap Questioner (V85-4-4 / gap E4, R-85-05).
 *
 * Given a blueprint inventory (observations + existing questions from
 * pass 1) and the reference architecture, asks targeted questions about
 * unknown or conflicting inventory observations.
 *
 * R-85-05 rules enforced:
 *   - Does NOT repeat questions the inventory already has evidence for
 *   - Only asks about slots where evidence is missing, conflicting, or
 *     stale
 *   - Customer answers retain interview provenance
 *   - Unknowns are prompted, not silently skipped
 *   - No invented coverage — questions only, never fabricated answers
 *
 * Runs through the V85-4-0 agent harness. Output writes to
 * `proposed_changes` queue (each question set is a proposal for editor
 * review before being sent to the customer).
 */
import { defineTemplate, derive, field } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
// ---- Agent definition -------------------------------------------------------
export const A6A_DEFINITION = {
    agent_id: 'A6a',
    prompt_version: '2026.09.20',
    queue_target: 'proposed_changes',
    schema_version: '1.0',
};
/** Output schema for harness validation. */
export const A6A_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['questions', 'evidence_summary'],
    properties: {
        questions: { type: 'array' },
        evidence_summary: { type: 'string' },
    },
};
const STRENGTH_RANK = {
    directly_observed: 3,
    inferred: 2,
    user_supplied: 1,
};
export function classifySlotEvidence(slotId, observations, asOf) {
    const slotObs = observations.filter((o) => o.slot_id === slotId);
    if (slotObs.length === 0) {
        return { slot_id: slotId, state: 'missing', observations: [] };
    }
    // Check for staleness
    const now = new Date(asOf).getTime();
    const anyStale = slotObs.some((o) => {
        if (!o.stale_after_days)
            return false;
        const obsTime = new Date(o.observation_time).getTime();
        const staleMs = o.stale_after_days * 86400000;
        return now - obsTime > staleMs;
    });
    // Check for conflicting evidence (different claims about the same slot
    // with different strengths or conflicting statements)
    const strengths = new Set(slotObs.map((o) => o.claim_strength));
    const hasConflict = slotObs.length >= 2 &&
        strengths.size >= 2 &&
        slotObs.some((o) => STRENGTH_RANK[o.claim_strength] <= 1);
    if (hasConflict) {
        return { slot_id: slotId, state: 'conflicting', observations: slotObs };
    }
    if (anyStale) {
        return { slot_id: slotId, state: 'stale', observations: slotObs };
    }
    // Check confidence: if all observations are low-confidence inferred, ask
    const allLowConf = slotObs.every((o) => o.claim_strength === 'inferred' && STRENGTH_RANK[o.claim_strength] <= 2);
    if (allLowConf && slotObs.every((o) => !o.source_type.startsWith('ci_'))) {
        return { slot_id: slotId, state: 'low_confidence', observations: slotObs };
    }
    return { slot_id: slotId, state: 'supported', observations: slotObs };
}
/**
 * Deterministic question generation: for each RA slot with missing,
 * conflicting, or stale evidence, generate a targeted question. Skips
 * slots that already have questions in `existingQuestions`.
 */
export function generateDeterministicQuestions(input, asOf) {
    const existingSlots = new Set(input.existing_questions.map((q) => q.slot_id));
    const questions = [];
    for (const slot of input.ra_slots) {
        // Don't duplicate questions already generated by inventory pass
        if (existingSlots.has(slot.slot_id))
            continue;
        const evidence = classifySlotEvidence(slot.slot_id, input.observations, asOf);
        if (evidence.state === 'supported')
            continue;
        const gapType = evidence.state === 'missing'
            ? 'missing'
            : evidence.state === 'conflicting'
                ? 'conflicting'
                : evidence.state === 'stale'
                    ? 'stale'
                    : 'low_confidence';
        const question = buildQuestion(slot, evidence, gapType);
        questions.push(question);
    }
    return questions;
}
function buildQuestion(slot, evidence, gapType) {
    const questionId = `q-${slot.slot_id}-${gapType}-${Date.now().toString(36)}`;
    let question;
    let reason;
    switch (gapType) {
        case 'missing':
            question = `What tool or process fills the "${slot.role}" capability in your environment?`;
            reason = `No evidence observed for slot "${slot.slot_id}" (${slot.role})`;
            break;
        case 'conflicting':
            question = `We see conflicting signals about "${slot.role}" — ` +
                `${evidence.observations.map((o) => o.claim).join('; ')}. ` +
                `Which best describes your current setup?`;
            reason = `Conflicting evidence for slot "${slot.slot_id}": ` +
                `${evidence.observations.length} observations with different claim strengths`;
            break;
        case 'stale':
            question = `Your "${slot.role}" evidence is older than expected. ` +
                `Is ${evidence.observations[0]?.claim ?? 'this capability'} still in use?`;
            reason = `Evidence for slot "${slot.slot_id}" exceeds staleness threshold`;
            break;
        case 'low_confidence':
            question = `We inferred "${slot.role}" from tool names. ` +
                `Can you confirm ${evidence.observations[0]?.claim ?? 'this capability'} ` +
                `is actively used for this purpose?`;
            reason = `Only low-confidence inferred evidence for slot "${slot.slot_id}"`;
            break;
    }
    return { slot_id: slot.slot_id, question, reason, gap_type: gapType, question_id: questionId };
}
// ---- Agent function ---------------------------------------------------------
/**
 * The A6a agent function. Runs deterministic analysis first, then
 * optionally enriches with LLM-generated follow-up questions for
 * nuanced gaps. The LLM is NOT required — deterministic questions
 * are always present as the baseline.
 */
export const gapQuestionerAgent = async (llm, input) => {
    const data = input.data;
    const asOf = new Date().toISOString();
    // 1. Deterministic question generation
    const deterministicQuestions = generateDeterministicQuestions(data, asOf);
    // 2. Optionally enrich with LLM (graceful degradation)
    let evidenceSummary = `Deterministic analysis: ${deterministicQuestions.length} gaps identified across ${data.ra_slots.length} slots.`;
    let llmQuestions = [];
    try {
        // ADR-0073: through the egress gateway; a refusal keeps the deterministic questions.
        const r = await llm.run({
            task: 'agent.a6a.gap_questions', template: A6A_TEMPLATE, slots: promptSlots(data, deterministicQuestions),
            schemaHint: A6A_OUTPUT_SCHEMA,
        });
        if (r.status !== 'ok')
            throw new LlmRefusedError(r);
        const result = { data: r.output };
        if (result.data) {
            evidenceSummary = result.data.evidence_summary || evidenceSummary;
            // Validate LLM questions: only keep those for slots we actually
            // have gaps for and that don't duplicate deterministic questions
            const deterministicSlots = new Set(deterministicQuestions.map((q) => q.slot_id));
            const validSlots = new Set(data.ra_slots.map((s) => s.slot_id));
            for (const q of result.data.questions ?? []) {
                if (!validSlots.has(q.slot_id))
                    continue; // Fabricated slot
                if (deterministicSlots.has(q.slot_id))
                    continue; // Already asked
                llmQuestions.push({
                    slot_id: q.slot_id,
                    question: q.question,
                    reason: q.reason,
                    gap_type: q.gap_type || 'missing',
                    question_id: `q-llm-${q.slot_id}-${Date.now().toString(36)}`,
                });
            }
        }
    }
    catch {
        // LLM failure: fall back to deterministic-only
        evidenceSummary = `Deterministic-only analysis (LLM unavailable): ${deterministicQuestions.length} gaps identified.`;
    }
    const allQuestions = [...deterministicQuestions, ...llmQuestions];
    const provenance = {
        questions: {
            source: 'blueprint-inventory-observations',
            span: `${allQuestions.length} questions for ${new Set(allQuestions.map((q) => q.slot_id)).size} slots`,
            confidence: 0.9,
        },
        evidence_summary: {
            source: 'deterministic-analysis',
            span: evidenceSummary.slice(0, 100),
            confidence: 0.95,
        },
    };
    const output = {
        target: 'proposed_changes',
        proposal: {
            questions: allQuestions,
            evidence_summary: evidenceSummary,
        },
        provenance,
    };
    return output;
};
/** ADR-0073: the A6a prompt as a reviewed template; inventory observations and gaps are tenant data. */
const A6A_PROMPT = [
    'You are an AI governance gap analyst. Review the following inventory observations and identified gaps.',
    'Generate additional targeted questions ONLY for gaps not already covered by the deterministic analysis.',
    'Do NOT ask about slots that have strong evidence. Do NOT fabricate coverage.',
    '',
    '## Observed Evidence',
    '{observed}',
    '',
    '## Already-Identified Gaps',
    '{gaps}',
    '',
    'Return JSON: { "questions": [{ "slot_id", "question", "reason", "gap_type" }], "evidence_summary": "..." }',
].join('\n');
export const A6A_TEMPLATE = defineTemplate('agent.a6a.gap_questions', '2026.09.20', A6A_PROMPT, ['observed', 'gaps']);
function promptSlots(input, deterministicQuestions) {
    const observed = input.observations.map((o) => `- Slot: ${o.slot_id} | Claim: ${o.claim} | Strength: ${o.claim_strength} | Source: ${o.source_type}`);
    const gaps = deterministicQuestions.map((q) => `- Slot: ${q.slot_id} | Gap: ${q.gap_type} | Reason: ${q.reason}`);
    return {
        observed: field('blueprint.inventory', observed.join('\n')),
        gaps: derive('blueprint.inventory', gaps.join('\n'), ['blueprint.inventory']),
    };
}
//# sourceMappingURL=agent-gap-questioner-service.js.map